import { useEffect, useRef, useState } from 'react';
import { Client } from '@stomp/stompjs';
import Constants from '../constants';

// ─── Статусы соединения ───────────────────────────────────────────────────────
export const WS_STATUS = {
	/** Первичное подключение ещё не завершено */
	CONNECTING: 'CONNECTING',
	/** Соединение активно */
	CONNECTED: 'CONNECTED',
	/** Соединение потеряно, идёт переподключение */
	RECONNECTING: 'RECONNECTING',
	/** Соединение окончательно потеряно (компонент unmounted) */
	DISCONNECTED: 'DISCONNECTED',
};

// Задержки между попытками reconnect: 2s → 4s → 8s → 15s → 30s
const BACKOFF_DELAYS = [2000, 4000, 8000, 15000, 30000];

function getBackoffDelay(attempt) {
	return BACKOFF_DELAYS[Math.min(attempt, BACKOFF_DELAYS.length - 1)];
}

// Сервер шлёт STOMP heartbeat каждые 25с. Если кадров (включая heartbeat) нет
// дольше этого порога — соединение «тихо умерло» (half-open), пересоздаём.
const ACTIVITY_TIMEOUT = 60000;
// Период проверки активности watchdog'ом
const WATCHDOG_INTERVAL = 15000;

/**
 * Хук для подписки на STOMP-топик через WebSocket/SockJS.
 *
 * Особенности:
 * - Exponential backoff при переподключении
 * - Отслеживает состояние соединения (CONNECTING → CONNECTED → RECONNECTING)
 * - Чисто освобождает ресурсы при unmount
 * - Не пересоздаёт клиент при изменении onMessage (используется ref)
 *
 * @param {string}   topic     - STOMP топик, напр. '/topic/queue-events'
 * @param {function} onMessage - callback(parsedData) при каждом сообщении
 * @returns {{ status: string }} - текущий статус соединения (WS_STATUS)
 */
export function useWebSocket(topic, onMessage) {
	const [status, setStatus] = useState(WS_STATUS.CONNECTING);

	// Всегда используем актуальный callback без пересоздания эффекта
	const onMessageRef = useRef(onMessage);
	useEffect(() => {
		onMessageRef.current = onMessage;
	});

	// Время последнего кадра от сервера (включая heartbeat) — для watchdog'а
	const lastActivityRef = useRef(Date.now());

	useEffect(() => {
		let mounted = true;
		let attempt = 0;
		let connected = false;
		let reconnectTimer = null;
		let client = null;
		let sub = null;

		function cleanup() {
			clearTimeout(reconnectTimer);
			try {
				sub?.unsubscribe();
			} catch {}
			try {
				client?.deactivate();
			} catch {}
			sub = null;
			client = null;
		}

		function scheduleReconnect() {
			if (!mounted) return;
			connected = false;
			const delay = getBackoffDelay(attempt);
			attempt++;
			setStatus(WS_STATUS.RECONNECTING);
			console.info(
				`[WS] Reconnect attempt ${attempt} in ${delay / 1000}s (topic: ${topic})`
			);
			reconnectTimer = setTimeout(() => {
				if (mounted) connect(); // eslint-disable-line no-use-before-define
			}, delay);
		}

		function connect() {
			if (!mounted) return;

			// Очищаем предыдущий клиент если есть
			try {
				sub?.unsubscribe();
			} catch {}
			try {
				client?.deactivate();
			} catch {}

			client = new Client({
				webSocketFactory: () => {
					const ws = new WebSocket(Constants.WS_BASE_URL);
					// Любой кадр (включая STOMP heartbeat) = соединение живо
					ws.addEventListener('message', () => {
						lastActivityRef.current = Date.now();
					});
					return ws;
				},
				// Отключаем встроенный reconnect — управляем вручную
				reconnectDelay: 0,
				// Heartbeat 25s — стабильнее на мобильных и за прокси
				heartbeatIncoming: 25000,
				heartbeatOutgoing: 25000,
				// Отключаем debug-логи в production
				debug: process.env.NODE_ENV === 'development'
					? (str) => console.debug('[STOMP]', str)
					: () => {},
			});

			client.onConnect = () => {
				if (!mounted) return;
				attempt = 0; // сбрасываем счётчик при успешном подключении
				connected = true;
				lastActivityRef.current = Date.now();
				setStatus(WS_STATUS.CONNECTED);

				try {
					sub = client.subscribe(topic, (frame) => {
						if (!mounted) return;
						try {
							const data = JSON.parse(frame.body);
							onMessageRef.current?.(data);
						} catch (e) {
							console.error('[WS] JSON parse error:', e, frame.body);
						}
					});
				} catch (e) {
					console.error('[WS] Subscribe error:', e);
					scheduleReconnect();
				}
			};

			client.onStompError = (frame) => {
				const msg = frame?.headers?.message;
				console.error('[WS] STOMP broker error:', msg);
				scheduleReconnect();
			};

			client.onWebSocketClose = (ev) => {
				if (!mounted) return;
				// code 1000 = нормальное закрытие (мы вызвали deactivate при unmount)
				if (ev?.code === 1000) return;
				scheduleReconnect();
			};

			// onWebSocketError всегда сопровождается onWebSocketClose,
			// поэтому здесь только логируем, не планируем reconnect
			client.onWebSocketError = (ev) => {
				console.warn('[WS] WebSocket error:', ev?.type);
			};

			try {
				client.activate();
			} catch (e) {
				console.error('[WS] Activate error:', e);
				scheduleReconnect();
			}
		}

		connect();

		// ── Watchdog: ловим «тихо умерший» (half-open) сокет ──────────────────
		// Если при активном (по нашему мнению) соединении нет ни одного кадра
		// дольше ACTIVITY_TIMEOUT (heartbeat идёт каждые 25с) — пересоздаём.
		const watchdog = setInterval(() => {
			if (!mounted || !connected) return;
			const silent = Date.now() - lastActivityRef.current;
			if (silent > ACTIVITY_TIMEOUT) {
				console.warn(`[WS] No activity for ${silent}ms — forcing reconnect`);
				connected = false;
				attempt = 0;
				connect();
			}
		}, WATCHDOG_INTERVAL);

		// Пробуждение вкладки / восстановление сети: таймеры могли быть заморожены
		// фоновым троттлингом браузера — проверяем и переподключаемся, если протухло.
		function onWake(reason) {
			if (!mounted) return;
			const stale = Date.now() - lastActivityRef.current > ACTIVITY_TIMEOUT;
			if (!connected || stale) {
				console.info(`[WS] Wake (${reason}) — reconnecting`);
				connected = false;
				attempt = 0;
				connect();
			}
		}
		const onVisible = () => {
			if (document.visibilityState === 'visible') onWake('visibility');
		};
		const onOnline = () => onWake('online');
		document.addEventListener('visibilitychange', onVisible);
		window.addEventListener('online', onOnline);

		return () => {
			mounted = false;
			setStatus(WS_STATUS.DISCONNECTED);
			clearInterval(watchdog);
			document.removeEventListener('visibilitychange', onVisible);
			window.removeEventListener('online', onOnline);
			cleanup();
		};
	}, [topic]); // пересоздаём только если меняется топик

	return { status };
}
